import { Pencil, Plus, LayoutGrid, Filter, Printer, Check, MoreHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import MainContainer from '../../components/containers/MainContainer';
import NLeftPanel from '../../components/NLeftPanel';
import { apiService } from '../../utils/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { SearchContext } from '../project/building/Building';
import TreeItem, { TreeItemButton } from './features/DocumentationTree';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/Button';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/Popover';

import useGetDocumentationDetail from './hooks/getDcomentationDetail';
import useGetDocumentation from './hooks/getGetDocumentation';
import {
  useGetDocumentationNotifications,
  useGetSpecificNotifications,
} from './hooks/getNotifications';
import { usePdfStatusSocket } from './hooks/usePdfStatusSocket';
import { useGetContractModels } from './hooks/getModels';
import DocDialog from './modals/CreateDocDialog';
import PartModal from './modals/PartModal';

function updateStatus(tree, statusData) {
  return tree?.map((item) => {
    if (item.id === statusData.documentID) {
      return {
        ...item,
        status: statusData.status,
        last_revision: {
          ...item.last_revision,
          tasks: [{ file: statusData?.file ?? '' }],
        },
      };
    } else if (item.children.length > 0) {
      return {
        ...item,
        children: updateStatus(item.children, statusData),
      };
    }
    return item;
  });
}

function updateFilter(tree, docId, filterId) {
  return tree?.map((item) => {
    if (item.id === docId) {
      return {
        ...item,
        filter: filterId,
      };
    } else if (item.children.length > 0) {
      return {
        ...item,
        children: updateFilter(item.children, docId, filterId),
      };
    }
    return item;
  });
}

const Documentation = () => {
  const { current_company } = useSelector((state) => state.auth);
  const { contract, phaseId, modelId } = useSelector((state) => state.contract);
  const contractId = contract?.id ?? null;

  const [search, setSearch] = useState(null);
  const [filter, setFilter] = useState({ model: modelId ?? undefined });
  const [activeDocumentationId, setActiveDocumentationId] = useState(null);
  const [activeDocumentId, setActiveDocumentId] = useState(null);

  const { data: tree } = useGetDocumentationDetail(activeDocumentationId);
  const { data: documentation } = useGetDocumentation(activeDocumentationId);
  const { data: notifications } = useGetDocumentationNotifications(documentation?.id);

  const { data: unResolvedNotifications } = useGetSpecificNotifications(
    notifications?.map(({ notificationId }) => notificationId)
  );

  const queryKey = 'documentation';
  const queryClient = useQueryClient();

  // Realtime PDF status over the `pdf:{id}` gateway topic (replaces polling).
  // `notificationId` is the Pdf UUID; a push refetches the task query, which
  // carries the download URL for completed PDFs.
  const pdfIds: string[] = notifications?.map(({ notificationId }) => notificationId) ?? [];
  const refetchPdfStatus = useCallback(() => {
    queryClient.invalidateQueries('specific-notifications');
  }, [queryClient]);
  usePdfStatusSocket(pdfIds, refetchPdfStatus);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [partModalOpen, setPartModalOpen] = useState(false);

  const [edit, setEdit] = useState(false);
  const { data: models } = useGetContractModels();

  // The panel's model filter follows the globally selected model until the
  // user overrides it in the filter popover; a global change resets it back.
  useEffect(() => {
    setFilter((prev) => ({ ...prev, model: modelId ?? undefined }));
  }, [modelId]);

  useEffect(() => {
    setActiveDocumentationId(null);
  }, [contractId, filter.model]);

  useEffect(() => {
    if (unResolvedNotifications && tree) {
      const unresolved = [];
      queryClient.setQueryData(['documentationTree', activeDocumentationId], () => {
        let newTree = [...tree];

        unResolvedNotifications.map((statusData) => {
          newTree = updateStatus(newTree, statusData);

          // Terminal states: R (ready), E (error), MP (missing previews - failed).
          const terminal = ['R', 'E', 'MP'];
          if (!terminal.includes(statusData?.status)) {
            unresolved.push(statusData);
          }
        });

        return newTree;
      });

      queryClient.setQueryData(['notifications', documentation?.id], () => {
        return unresolved;
      });
    }
  }, [unResolvedNotifications, queryClient, tree]);

  const updateTreeItems = useMutation({
    mutationFn: ({ parent, next }: { parent: any; next: any }) => {
      return apiService.get(`/api/v1/documentation/${activeDocumentationId}/treeview/`, {
        params: { parent, page: next, page_size: 10 },
      });
    },
    onSuccess: ({ data }, parent) => {
      queryClient.setQueryData(['documentation', activeDocumentationId], (prev) => {
        const updatedTree = iterateOverRootOfTree(prev, parent, data);
        return updatedTree;
      });
    },
  });

  const createDocPart = useMutation({
    mutationFn: (values: any) => {
      return apiService.post(`/api/v1/document/`, {
        ...values,
        documentation: activeDocumentationId,
        parent: activeDocumentId,
      });
    },
    onSuccess: () => {
      //TODO mrknout proc to freezen appku
      // queryClient.setQueryData(['documentationTree', activeDocumentationId], (oldData) => {
      //   return addItemToTree(oldData, data);
      // });

      queryClient.invalidateQueries(['documentationTree', activeDocumentationId]);
      setPartModalOpen(false);
    },
    onError: (err) => {
      console.error(err);
    },
  });

  const createDoc = useMutation({
    mutationFn: (values: any) => {
      return apiService.post(`/api/v1/documentation/`, {
        ...values,
        company: current_company,
        contract: contractId,
        phase: phaseId,
      });
    },
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries(queryKey);
      setDocDialogOpen(false);
      setActiveDocumentationId(data.id);
    },
  });

  const editDoc = useMutation({
    mutationFn: ({ id, values }: { id: any; values: any }) => {
      return apiService.patch(`/api/v1/documentation/${id}/`, values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
      setDocDialogOpen(false);
      setEdit(false);
    },
  });

  const handlePrintPdf = async (id) => {
    if (!id) {
      return;
    }

    try {
      await apiService.get(`/api/v1/document/${id}/generate-pdf/`, { silentError: true });
      queryClient.invalidateQueries(['notifications', activeDocumentationId]);
    } catch (e) {
      toast.error('Tisk se nezdařil', {
        description: getApiErrorMessage(e, 'Nepodařilo se vygenerovat PDF.'),
      });
    }
  };

  const handleAddPart = (id, values) => {
    createDocPart.mutate(values);
  };

  const handleDocSubmit = (id, values) => {
    if (!id) {
      createDoc.mutate(values);
    } else {
      editDoc.mutate({ id, values });
    }
  };

  const updateItemInTree = (node, id, nextItems) => {
    if (node.id === id) {
      node.children = node.children.concat(nextItems);
    }

    if (node.children !== undefined && node.children.length > 0) {
      for (let i = 0; i < node.children.length; i++) {
        node.children[i] = updateItemInTree(node.children[i], id, nextItems);
      }
    }

    return node;
  };

  const iterateOverRootOfTree = (tree, id, dataForUpdate) => {
    let result = [];
    if (Array.isArray(tree) && tree.length > 0) {
      for (let i = 0; i <= tree.length - 1; i++) {
        result = result.concat(updateItemInTree(tree[i], id, dataForUpdate));
      }
    }

    return result;
  };

  const getClassificationChildren = (parent) => {
    const startFetchRutine = async (next) => {
      if (!next) return null;

      updateTreeItems.mutate({ parent, next });
    };
    startFetchRutine(1);
  };

  const itemTrea = (item) => {
    const controls = [];

    if (item.document_type !== 'SH' && item.document_type !== 'DR') {
      controls.push(
        <TreeItemButton
          key={2}
          text="Přidat"
          onClick={() => {
            setActiveDocumentId(item.id);
            setPartModalOpen(true);
          }}
        />
      );
    } else {
      controls.push(
        <TreeItemButton
          disabled={item.status === 'G' || item.status === 'P' || item.status === 'WP'}
          key={1}
          onClick={() => handlePrintPdf(item.id)}
          text="Tisk"
        />
      );
    }

    if (item.children.length === 0) {
      return (
        <TreeItem
          item={item}
          key={item.id}
          type={item.document_type}
          content={item.title}
          filterId={item.filter}
          documentId={item.id}
          documentationId={activeDocumentationId}
          updateFilter={(filterId) => updateFilter(tree, item.id, filterId)}
          updateDocument={() => {
            setPartModalOpen(true);
          }}
          files={
            item.file_list
              ? item.file_list.map((ele, i) => (
                  <a
                    key={i}
                    data-testid={`documentation-file-link-${i}`}
                    // className="link-image-design-form"
                    href="#"
                    onClick={() => {
                      // if (ele.type === 'pdf') openInNewTab(ServerPath._currentValue + ele.pdf);
                      // else if (ele.type === 'dwg')
                      //   this.downloadFileWithURI(ServerPath._currentValue + ele.pdf, 'file');
                      // return false;
                    }}
                  >
                    {/* {IconView(ele)} */}
                  </a>
                ))
              : ''
          }
          onExpand={() => {
            getClassificationChildren(item.id);
          }}
          classification={item.classification}
          revit_UUID={item.revit_UUID}
          scale={item.type === 'D' ? item.scale : ''}
          created_on={item.type === 'D' ? item.last_version_created_on : ''}
          is_officially_released={item.type === 'D' ? item.is_officially_released : ''}
          status={item.status}
          tasks={item.last_revision?.tasks}
          last_revision={item.type === 'D' ? item.last_revision : ''}
          last_version={item.type === 'D' ? item.last_version : ''}
          controls={controls}
        />
      );
    }

    // let params = makeContent(item);
    return (
      <TreeItem
        item={item}
        key={item.id}
        type={item.document_type}
        documentId={item.id}
        content={item.title}
        documentationId={activeDocumentationId}
        filterId={item.filterId}
        updateFilter={(filterId) => updateFilter(tree, item.id, filterId)}
        updateDocument={() => {
          setPartModalOpen(true);
        }}
        onExpand={() => {
          getClassificationChildren(item.id);
        }}
        status={item.status}
        tasks={item.last_revision?.tasks}
        controls={controls}
        canHide
        open
      >
        {item.children.map((child) => itemTrea(child))}
      </TreeItem>
    );
  };

  const makeTree = (data) => {
    return data?.map((ele) => itemTrea(ele));
  };

  const countItems = (items: any[], type?: string): number => {
    if (!items) return 0;
    return items.reduce((acc, item) => {
      const match = type ? item.document_type === type : true;
      const childCount = item.children ? countItems(item.children, type) : 0;
      return acc + (match ? 1 : 0) + childCount;
    }, 0);
  };

  const countByStatus = (items: any[], status: string): number => {
    if (!items) return 0;
    return items.reduce((acc, item) => {
      const match = item.status === status ? 1 : 0;
      const childCount = item.children ? countByStatus(item.children, status) : 0;
      return acc + match + childCount;
    }, 0);
  };

  const folderCount = useMemo(
    () => (tree ? countItems(tree, 'PA') + countItems(tree, 'SE') : 0),
    [tree]
  );
  const outputCount = useMemo(
    () => (tree ? countItems(tree, 'SH') + countItems(tree, 'DR') : 0),
    [tree]
  );
  const readyCount = useMemo(() => (tree ? countByStatus(tree, 'R') : 0), [tree]);

  return (
    <SearchContext.Provider
      value={{
        search,
        filter,
        setSearch,
        setFilter,
      }}
    >
      <MainContainer>
        <div className="flex w-full">
          <NLeftPanel
            queryKey={queryKey}
            makeActive={setActiveDocumentationId}
            active={activeDocumentationId}
            onSearch={() => {}}
            addItem={() => setDocDialogOpen(true)}
            onFilter={() => {}}
            defaultFilterValues={{ is_active: [true] }}
            title="Dokumentace"
            permission={2}
            filteredValues={{
              is_active: [
                { value: true, title: 'Aktivní' },
                { value: false, title: 'Neaktivní' },
              ],
              model: {
                type: 'radio',
                title: 'Filtrovat podle modelu',
                options: (models ?? []).map((m) => ({ value: m.id, title: m.title })),
              },
            }}
          />
          {activeDocumentationId && (
            <div className="flex-1 flex flex-col min-w-0 @container">
              {/* Sub-header with stats and actions */}
              <div className="h-14 border-b border-neutral-150 bg-white flex items-center px-6 gap-3">
                <div>
                  <h2 className="text-base font-semibold tracking-tight text-neutral-900">
                    Dokumentace
                  </h2>
                  <div className="text-[11px] text-neutral-500 -mt-0.5">
                    Strom výstupů, šablon a tiskových konfigurací
                  </div>
                </div>
                <div className="ml-4 hidden @2xl:flex items-center gap-2">
                  <Badge variant="outline" size="md" className="font-mono">
                    {folderCount} složek
                  </Badge>
                  <Badge variant="outline" size="md" className="font-mono">
                    {outputCount} výstupů
                  </Badge>
                  <Badge variant="success" size="md" className="font-mono">
                    <Check size={10} /> {readyCount} připraveno
                  </Badge>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {/* Sekundární akce – inline na širokém sloupci */}
                  <div className="hidden @4xl:flex items-center gap-2">
                    <Button variant="outline" size="sm" data-testid="documentation-view-btn">
                      <LayoutGrid size={13} /> Pohled
                    </Button>
                    <Button variant="outline" size="sm" data-testid="documentation-filter-btn">
                      <Filter size={13} /> Filtrovat
                    </Button>
                    <div className="w-px h-6 bg-neutral-150 mx-1" />
                    <Button variant="outline" size="sm" data-testid="documentation-print-all-btn">
                      <Printer size={13} /> Tisknout vše
                    </Button>
                  </div>

                  {/* Sekundární akce – sbalené do popoveru na úzkém sloupci */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        className="@4xl:hidden"
                        aria-label="Další akce"
                        data-testid="documentation-actions-more-btn"
                      >
                        <MoreHorizontal size={15} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-48 p-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        data-testid="documentation-view-btn-popover"
                      >
                        <LayoutGrid size={13} /> Pohled
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        data-testid="documentation-filter-btn-popover"
                      >
                        <Filter size={13} /> Filtrovat
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start"
                        data-testid="documentation-print-all-btn-popover"
                      >
                        <Printer size={13} /> Tisknout vše
                      </Button>
                    </PopoverContent>
                  </Popover>

                  <Button
                    size="sm"
                    data-testid="documentation-add-part-btn"
                    onClick={() => {
                      setActiveDocumentId(null);
                      setPartModalOpen(true);
                    }}
                  >
                    <Plus size={13} />
                    <span className="hidden @xl:inline">Nový výstup</span>
                  </Button>
                </div>
              </div>

              {/* Tree column header */}
              <div className="h-9 border-b border-neutral-150 bg-neutral-25 flex items-center px-3 text-[11px] uppercase tracking-wider font-medium text-neutral-500">
                <div className="w-5 mr-2" />
                <div className="ml-9">Detail</div>
                <div className="ml-auto flex items-center gap-1.5">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    data-testid="documentation-add-part-btn-2"
                    onClick={() => {
                      setActiveDocumentId(null);
                      setPartModalOpen(true);
                    }}
                  >
                    <Plus size={13} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    data-testid="documentation-edit-btn"
                    onClick={() => {
                      setEdit(true);
                      setDocDialogOpen(true);
                    }}
                  >
                    <Pencil size={13} />
                  </Button>
                </div>
              </div>

              {/* Tree */}
              <div className="flex-1 overflow-y-auto bg-white">
                {makeTree(tree)}

                {/* Bottom hint */}
                <div className="px-3 py-3 text-center text-[11px] text-neutral-400">
                  Konec stromu.{' '}
                  <button
                    className="text-primary-600 hover:underline"
                    data-testid="documentation-add-folder-btn"
                    onClick={() => setPartModalOpen(true)}
                  >
                    + Přidat novou složku
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-neutral-150 bg-neutral-25 h-9 flex items-center justify-between px-4 text-[11px] text-neutral-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Synchronizováno
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span>Klávesové zkratky:</span>
                  <kbd className="px-1.5 py-0.5 rounded bg-white border border-neutral-200 text-[10px] font-mono">
                    ↑↓
                  </kbd>
                  <span>navigace</span>
                  <kbd className="px-1.5 py-0.5 rounded bg-white border border-neutral-200 text-[10px] font-mono">
                    ⏎
                  </kbd>
                  <span>otevřít</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </MainContainer>

      <DocDialog
        visible={docDialogOpen}
        onClose={() => {
          setDocDialogOpen(false);
          setEdit(false);
        }}
        formProps={edit ? documentation : null}
        onSubmit={handleDocSubmit}
      />

      {partModalOpen && (
        <PartModal
          visible={partModalOpen}
          onSubmit={handleAddPart}
          documentationId={activeDocumentationId}
          documentId={activeDocumentId}
          onClose={() => {
            setActiveDocumentId(null);
            setPartModalOpen(false);
          }}
        />
      )}
    </SearchContext.Provider>
  );
};

export default Documentation;
